import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Box, Chip, Stack, TextField, FormHelperText } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSelect, RHFSwitch, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { Track } from 'src/@types/tracks';
import { Category } from 'src/@types/Category';
import { UploadAvatar, UploadMultiFile } from 'src/components/upload';

// ----------------------------------------------------------------------

type Props = {
  isEdit: boolean;
  currentTrack?: Track;
  handleClose(): void;
};

export default function TrackNewEditForm({ isEdit, currentTrack, handleClose }: Props) {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  useEffect(() => {
    getCategories();
  }, []);
  const onUploadCertificate = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.DOCUMENT, form);
    return response.data;
  };
  const getCategories = async () => {
    const response = await axiosInstance.get(APIS.CATEGORIES.LIST);
    setCategories(response.data.data);
  };
  const { enqueueSnackbar } = useSnackbar();
  const expression = /^(ftp|http|https):\/\/[^ "]+$/;

  const NewTrackSchema = Yup.object().shape({
    title: Yup.string().min(3).required('Track title is required'),
    active: Yup.boolean(),
    isDiplomaLive: Yup.boolean(),
    isCareerTrack: Yup.boolean(),
    order: Yup.number().typeError('Display order must be a number').min(0),
    certificateUrl: Yup.string(),
    description: Yup.string().min(3).required('description is Required'),
    category: Yup.string().required('Path is Required'),
    image: Yup.string(),
    // Optional, as the API has it: 13 of the 16 live tracks have no brief video,
    // and requiring one here made every edit to them (SOC, Web, Data…) fail
    // silently. A value that IS typed must still be a link.
    briefVideo: Yup.string().matches(expression, {
      message: 'Video Url must be a valid url',
      excludeEmptyString: true,
    }),
    icon: Yup.string(),
    keywords: Yup.array().min(1).required('keywords is required'),
  });
  const defaultValues: any = useMemo(
    () => ({
      title: currentTrack?.title || '',
      title_ar: currentTrack?.title_ar || '',
      image: currentTrack?.image || '',
      briefVideo: currentTrack?.briefVideo || '',
      icon: currentTrack?.icon || '',
      certificateUrl: currentTrack?.certificateUrl || '',
      description: currentTrack?.description || '',
      description_ar: currentTrack?.description_ar || '',
      active: currentTrack?.active || false,
      isDiplomaLive: currentTrack?.isDiplomaLive || false,
      isCareerTrack: currentTrack?.isCareerTrack || false,
      order: currentTrack?.order ?? 0,
      category: currentTrack?.category?._id || '',
      keywords: currentTrack?.keywords || [],
      keywords_ar: currentTrack?.keywords_ar || [],
    }),
    [currentTrack]
  );

  const methods = useForm<any>({
    resolver: yupResolver(NewTrackSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentTrack) {
      reset({ ...currentTrack, category: currentTrack?.category?._id });
    }
  }, [isEdit, currentTrack]);

  const onSubmit = async (data: any) => {
    // title_ar is required by the API — fall back to the English title when the
    // Arabic title is left empty.
    if (!data.title_ar) data.title_ar = data.title;
    if (!data.description_ar) delete data?.description_ar;
    if (!data.keywords_ar) delete data?.keywords_ar;
    // The API validates `order` as a number; the input hands back a string.
    data.order = Number(data.order) || 0;
    try {
      if (currentTrack)
        await axiosInstance.patch(APIS.TRACKS.PATCH(currentTrack._id as string), data);
      else await axiosInstance.post(APIS.TRACKS.ADD, data);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      if (err?.message) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      }

      if (err?.messages.length && err?.messages[0]) {
        enqueueSnackbar(err?.messages.length && err?.messages[0], { variant: 'error' });
      } else {
        enqueueSnackbar(!isEdit ? 'Unable to create Track!' : 'Unable to edit Track', {
          variant: 'error',
        });
      }
    }
  };

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  useEffect(() => {
  console.log("Errorrs",errors)
},[errors])
  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Track' : ' Create Track'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack spacing={3}>
            <Stack direction={'row'} justifyContent={'center'} spacing={9}>
              <Stack direction={'column'}>
                <UploadAvatar
                  label="Track Icon"
                  accept={{ 'image/*': [] }}
                  error={errors.icon ? true : false}
                  file={values.icon}
                  onDropAccepted={async (file) => {
                    let response = await uploadImage(file[0]);
                    setValue('icon', response?.url);
                  }}
                />
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.icon && String(errors?.icon?.message)}
                </p>
              </Stack>
              <Stack>
                <UploadAvatar
                  label="Track Image"
                  accept={{ 'image/*': [] }}
                  error={errors.image ? true : false}
                  file={values.image}
                  onDropAccepted={async (file) => {
                    let response = await uploadImage(file[0]);
                    setValue('image', response?.url);
                  }}
                />
                <p
                  style={{
                    color: '#FF4842',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    marginTop: '10px',
                  }}
                >
                  {errors?.image && String(errors?.image?.message)}
                </p>
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="title" label="English Track Title" />
              <RHFTextField name="title_ar" label="Arabic Track Title" />
            </Box>
            {/* Phase: user segmentation — subscribers of this track = "Diploma Live" */}
            <RHFSwitch
              name="isDiplomaLive"
              label="Diploma Live track (subscribers count as the Diploma Live segment)"
            />
            {/* The student site's Tracks page leads with career tracks (SOC, Web, Data…);
                everything else (tips, free sessions, crash courses) is listed under "Start small". */}
            <RHFSwitch
              name="isCareerTrack"
              label="Career track (shown first on the website's Tracks page; off = tips, free sessions or crash courses)"
            />
            <RHFTextField
              name="order"
              type="number"
              label="Display order (lower numbers are shown first on the website)"
            />
            <RHFSelect value={values.category} name="category" label="Path" placeholder="Path">
              <option value="" />
              {categories?.map((option: Category) => (
                <option key={option._id} value={option._id}>
                  {option.title}
                </option>
              ))}
            </RHFSelect>
            <RHFTextField name="briefVideo" label="Track Brief Video Url" />

            <RHFTextField
              multiline
              minRows={2}
              name="description"
              maxRows={3}
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              name="description_ar"
              maxRows={3}
              label="Arabic Description"
            />
            <div>
              <Autocomplete
                multiple
                id="keywords"
                options={[]}
                value={values.keywords}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      key={index}
                      variant="outlined"
                      label={option}
                      onDelete={(e) => {
                        values.keywords.splice(index, 1);

                        setValue('keywords', values.keywords);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    error={values.keywords.length == 0 && errors.keywords ? true : false}
                    {...params}
                    name={'keywords'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords', [...values.keywords, event?.target?.value as any]);
                      }
                      if (e.key == 'Backspace') {
                        const value = values?.keywords;
                        value?.pop();

                        setValue('keywords', value);
                      }
                    }}
                    variant="outlined"
                    label="Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
              {values.keywords.length == 0 && (
                <span style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '5px' }}>
                  {errors?.keywords?.message as any}
                </span>
              )}
            </div>
            <div>
              <Autocomplete
                multiple
                id="keywords_ar"
                options={[]}
                value={values?.keywords_ar ?? []}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      key={index}
                      variant="outlined"
                      label={option}
                      onDelete={(e) => {
                        values?.keywords_ar.splice(index, 1);

                        setValue('keywords_ar', values?.keywords_ar);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={'keywords_ar'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue(
                          'keywords_ar',
                          values?.keywords_ar
                            ? [...values?.keywords_ar, event?.target?.value as any]
                            : [event?.target?.value as any]
                        );
                      }
                      if (e.key == 'Backspace') {
                        const value = values?.keywords_ar;
                        value?.pop();

                        setValue('keywords_ar', value);
                      }
                    }}
                    variant="outlined"
                    label="Arabic Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
            </div>
            <UploadMultiFile
              error={
                errors.certificateUrl && (values.certificateUrl == '' || !values.certificateUrl)
                  ? true
                  : false
              }
              disableControl={true}
              accept={{ 'application/pdf': ['application/pdf'] }}
              files={values.certificateUrl ? [values.certificateUrl] : []}
              maxFiles={1}
              label={'Select Or Drop Certificate Url'}
              onDropAccepted={async (file) => {
                const response = await onUploadCertificate(file[0]);
                setValue('certificateUrl', response.url);
              }}
            />
            {errors.certificateUrl && (values.certificateUrl == '' || !values.certificateUrl) && (
              <FormHelperText error sx={{ px: 2, textAlign: 'start' }}>
                {errors?.certificateUrl?.message ?? ('certificateUrl is required' as any)}
              </FormHelperText>
            )}
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Track' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
